import React, { useState, useEffect, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  Image,
  Modal,
  ScrollView,
  TextInput,
  Alert } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import {
  ArrowLeft,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  User,
  X,
  Check,
  Ban,
  MessageSquare } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { RootStackParamList } from '../types/navigation'
import {
  Button,
  ButtonText,
  Card,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

interface OfferItem {
  id: string
  title: string
  description: string | null
  price: number
  images: { id: string; image_url: string; sort_order: number }[]
}

interface Offer {
  id: string
  seller_id: string
  status: 'pending' | 'accepted' | 'rejected' | 'completed'
  total_price: number
  description: string | null
  seller_notes: string | null
  store_response: string | null
  responded_at: string | null
  created_at: string
  seller: {
    id: string
    email: string
    display_name: string | null
  }
  items: OfferItem[]
}

type FilterStatus = 'all' | 'pending' | 'accepted' | 'rejected' | 'completed'

export default function IncomingOffersScreen() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()
  const route = useRoute<RouteProp<RootStackParamList, 'IncomingOffers'>>()
  const { storeId } = route.params
  const { t } = useLanguage()

  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('pending')
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [responseModalVisible, setResponseModalVisible] = useState(false)
  const [responseAction, setResponseAction] = useState<'accept' | 'reject' | null>(null)
  const [responseMessage, setResponseMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const fetchOffers = useCallback(async () => {
    try {
      // Build query
      let query = supabase
        .from('seller_offers')
        .select(`
          id,
          seller_id,
          status,
          total_price,
          description,
          seller_notes,
          store_response,
          responded_at,
          created_at,
          users!seller_id (
            id,
            email,
            display_name
          )
        `)
        .eq('store_id', storeId)
        .order('created_at', { ascending: false })

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus)
      }

      const { data: offersData, error: offersError } = await query

      if (offersError) throw offersError

      // Fetch items for each offer
      const offersWithItems: Offer[] = []
      for (const offer of offersData || []) {
        const { data: items } = await supabase
          .from('offer_items')
          .select(`
            id,
            title,
            description,
            price,
            offer_images (
              id,
              image_url,
              sort_order
            )
          `)
          .eq('offer_id', offer.id)
          .order('created_at', { ascending: true })

        offersWithItems.push({
          id: offer.id,
          seller_id: offer.seller_id,
          status: offer.status,
          total_price: offer.total_price,
          description: offer.description,
          seller_notes: offer.seller_notes,
          store_response: offer.store_response,
          responded_at: offer.responded_at,
          created_at: offer.created_at,
          seller: {
            id: (offer.users as any).id,
            email: (offer.users as any).email,
            display_name: (offer.users as any).display_name },
          items: (items || []).map(item => ({
            id: item.id,
            title: item.title,
            description: item.description,
            price: item.price,
            images: (item.offer_images as any[]) || [] })) })
      }

      setOffers(offersWithItems)
    } catch (error) {
      console.error('Error fetching offers:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [storeId, filterStatus])

  useEffect(() => {
    fetchOffers()
  }, [fetchOffers])

  const onRefresh = () => {
    setRefreshing(true)
    fetchOffers()
  }

  const getStatusIcon = (status: Offer['status']) => {
    switch (status) {
      case 'pending':
        return <Clock size={20} color="#f59e0b" />
      case 'accepted':
        return <CheckCircle size={20} color="#22c55e" />
      case 'rejected':
        return <XCircle size={20} color="#ef4444" />
      case 'completed':
        return <CheckCircle size={20} color="#3b82f6" />
    }
  }

  const getStatusText = (status: Offer['status']) => {
    switch (status) {
      case 'pending':
        return t('offer_pending') || 'Odottaa vastausta'
      case 'accepted':
        return t('offer_accepted') || 'Hyväksytty'
      case 'rejected':
        return t('offer_rejected') || 'Hylätty'
      case 'completed':
        return t('offer_completed') || 'Valmis'
    }
  }

  const getStatusColor = (status: Offer['status']) => {
    switch (status) {
      case 'pending':
        return '#fef3c7'
      case 'accepted':
        return '#dcfce7'
      case 'rejected':
        return '#fee2e2'
      case 'completed':
        return '#dbeafe'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fi-FI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit' })
  }

  const openOfferDetail = (offer: Offer) => {
    setSelectedOffer(offer)
    setDetailModalVisible(true)
  }

  const openResponseModal = (action: 'accept' | 'reject') => {
    setResponseAction(action)
    setResponseMessage('')
    setResponseModalVisible(true)
  }

  const handleSubmitResponse = async () => {
    if (!selectedOffer || !responseAction) return

    setSubmitting(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { error } = await supabase
        .from('seller_offers')
        .update({
          status: responseAction === 'accept' ? 'accepted' : 'rejected',
          store_response: responseMessage || null,
          responded_at: new Date().toISOString(),
          responded_by: user.id,
          updated_at: new Date().toISOString() })
        .eq('id', selectedOffer.id)

      if (error) throw error

      // Update local state
      setSelectedOffer(prev => prev ? {
        ...prev,
        status: responseAction === 'accept' ? 'accepted' : 'rejected',
        store_response: responseMessage || null,
        responded_at: new Date().toISOString() } : null)

      setResponseModalVisible(false)
      fetchOffers()

      Alert.alert(
        t('success') || 'Onnistui',
        responseAction === 'accept'
          ? (t('offer_accepted_message') || 'Tarjous hyväksytty')
          : (t('offer_rejected_message') || 'Tarjous hylätty')
      )
    } catch (error) {
      console.error('Error updating offer:', error)
      Alert.alert(t('error') || 'Virhe', t('try_again') || 'Yritä uudelleen')
    } finally {
      setSubmitting(false)
    }
  }

  const handleMarkCompleted = async () => {
    if (!selectedOffer) return

    Alert.alert(
      t('mark_completed') || 'Merkitse valmiiksi',
      t('mark_completed_confirm') || 'Haluatko merkitä tarjouksen valmiiksi?',
      [
        { text: t('cancel') || 'Peruuta', style: 'cancel' },
        {
          text: t('confirm') || 'Vahvista',
          onPress: async () => {
            try {
              const { error } = await supabase
                .from('seller_offers')
                .update({
                  status: 'completed',
                  updated_at: new Date().toISOString() })
                .eq('id', selectedOffer.id)

              if (error) throw error

              setSelectedOffer(prev => prev ? { ...prev, status: 'completed' } : null)
              fetchOffers()
            } catch (error) {
              console.error('Error marking completed:', error)
              Alert.alert(t('error') || 'Virhe', t('try_again') || 'Yritä uudelleen')
            }
          } },
      ]
    )
  }

  const renderFilterTabs = () => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.filterContainer}
      contentContainerStyle={styles.filterContent}
    >
      {(['pending', 'accepted', 'rejected', 'completed', 'all'] as FilterStatus[]).map(status => (
        <TouchableOpacity
          key={status}
          style={[styles.filterTab, filterStatus === status && styles.filterTabActive]}
          onPress={() => setFilterStatus(status)}
        >
          <Text style={[styles.filterText, filterStatus === status && styles.filterTextActive]}>
            {status === 'all'
              ? (t('all') || 'Kaikki')
              : status === 'pending'
              ? (t('pending') || 'Odottavat')
              : status === 'accepted'
              ? (t('accepted') || 'Hyväksytyt')
              : status === 'rejected'
              ? (t('rejected') || 'Hylätyt')
              : (t('completed') || 'Valmiit')
            }
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  )

  const renderOfferCard = ({ item: offer }: { item: Offer }) => {
    const firstImage = offer.items[0]?.images[0]?.image_url

    return (
      <TouchableOpacity
        style={styles.offerCard}
        onPress={() => openOfferDetail(offer)}
        activeOpacity={0.7}
      >
        <View style={styles.offerHeader}>
          <View style={styles.offerImageContainer}>
            {firstImage ? (
              <Image source={{ uri: firstImage }} style={styles.offerImage} />
            ) : (
              <View style={styles.offerImagePlaceholder}>
                <Package size={24} color="#9ca3af" />
              </View>
            )}
          </View>
          <View style={styles.offerInfo}>
            <View style={styles.sellerRow}>
              <User size={14} color="#6b7280" />
              <Text style={styles.sellerName}>
                {offer.seller.display_name || offer.seller.email}
              </Text>
            </View>
            <Text style={styles.itemCount}>
              {offer.items.length} {offer.items.length === 1 ? (t('item_count_singular') || 'tuote') : (t('items_count_plural') || 'tuotetta')}
            </Text>
            <Text style={styles.dateText}>{formatDate(offer.created_at)}</Text>
          </View>
          <View style={styles.offerRight}>
            <Text style={styles.totalPrice}>{offer.total_price.toFixed(2)} €</Text>
            <ChevronRight size={20} color="#9ca3af" />
          </View>
        </View>

        <View style={styles.offerFooter}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(offer.status) }]}>
            {getStatusIcon(offer.status)}
            <Text style={styles.statusText}>{getStatusText(offer.status)}</Text>
          </View>
          {offer.status === 'pending' && (
            <View style={styles.quickActions}>
              <TouchableOpacity
                style={[styles.quickActionButton, styles.acceptButton]}
                onPress={(e) => {
                  e.stopPropagation()
                  setSelectedOffer(offer)
                  openResponseModal('accept')
                }}
              >
                <Check size={16} color="#ffffff" />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.quickActionButton, styles.rejectButton]}
                onPress={(e) => {
                  e.stopPropagation()
                  setSelectedOffer(offer)
                  openResponseModal('reject')
                }}
              >
                <Ban size={16} color="#ffffff" />
              </TouchableOpacity>
            </View>
          )}
        </View>
      </TouchableOpacity>
    )
  }

  const renderDetailModal = () => {
    if (!selectedOffer) return null

    return (
      <Modal
        visible={detailModalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setDetailModalVisible(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity
              onPress={() => setDetailModalVisible(false)}
              style={styles.closeButton}
            >
              <X size={24} color="#111827" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>{t('offer_details') || 'Tarjouksen tiedot'}</Text>
            <View style={{ width: 40 }} />
          </View>

          <ScrollView style={styles.modalContent}>
            {/* Seller info */}
            <View style={styles.detailSection}>
              <Text style={styles.sectionTitle}>{t('seller') || 'Myyjä'}</Text>
              <View style={styles.sellerInfoRow}>
                <User size={20} color="#6b7280" />
                <View style={styles.sellerInfoText}>
                  <Text style={styles.detailSellerName}>
                    {selectedOffer.seller.display_name || t('no_name') || 'Nimetön'}
                  </Text>
                  <Text style={styles.sellerEmail}>{selectedOffer.seller.email}</Text>
                </View>
              </View>
            </View>

            {/* Status */}
            <View style={styles.detailSection}>
              <Text style={styles.sectionTitle}>{t('status') || 'Tila'}</Text>
              <View style={[styles.statusBadgeLarge, { backgroundColor: getStatusColor(selectedOffer.status) }]}>
                {getStatusIcon(selectedOffer.status)}
                <Text style={styles.statusTextLarge}>{getStatusText(selectedOffer.status)}</Text>
              </View>
              {selectedOffer.responded_at && (
                <Text style={styles.respondedDate}>
                  {t('responded') || 'Vastattu'}: {formatDate(selectedOffer.responded_at)}
                </Text>
              )}
            </View>

            {/* Seller notes */}
            {selectedOffer.seller_notes && (
              <View style={styles.detailSection}>
                <Text style={styles.sectionTitle}>{t('seller_notes') || 'Myyjän viesti'}</Text>
                <View style={styles.notesBox}>
                  <Text style={styles.notesText}>{selectedOffer.seller_notes}</Text>
                </View>
              </View>
            )}

            {/* Store response */}
            {selectedOffer.store_response && (
              <View style={styles.detailSection}>
                <Text style={styles.sectionTitle}>{t('your_response') || 'Sinun viestisi'}</Text>
                <View style={styles.responseBox}>
                  <Text style={styles.responseText}>{selectedOffer.store_response}</Text>
                </View>
              </View>
            )}

            {/* Items */}
            <View style={styles.detailSection}>
              <Text style={styles.sectionTitle}>{t('items') || 'Tuotteet'}</Text>
              {selectedOffer.items.map((item, index) => (
                <View key={item.id} style={styles.itemCard}>
                  <View style={styles.itemHeader}>
                    <Text style={styles.itemNumber}>{index + 1}.</Text>
                    <Text style={styles.itemTitle}>{item.title}</Text>
                    <Text style={styles.itemPrice}>{item.price.toFixed(2)} €</Text>
                  </View>
                  {item.description && (
                    <Text style={styles.itemDescription}>{item.description}</Text>
                  )}
                  {item.images.length > 0 && (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.itemImages}>
                      {item.images.sort((a, b) => a.sort_order - b.sort_order).map(img => (
                        <Image key={img.id} source={{ uri: img.image_url }} style={styles.itemImage} />
                      ))}
                    </ScrollView>
                  )}
                </View>
              ))}
            </View>

            {/* Total */}
            <View style={styles.totalSection}>
              <Text style={styles.totalLabel}>{t('total') || 'Yhteensä'}</Text>
              <Text style={styles.totalValue}>{selectedOffer.total_price.toFixed(2)} €</Text>
            </View>

            {/* Action buttons */}
            {selectedOffer.status === 'pending' && (
              <View style={styles.actionButtons}>
                <TouchableOpacity
                  style={[styles.actionButton, styles.acceptActionButton]}
                  onPress={() => openResponseModal('accept')}
                >
                  <Check size={20} color="#ffffff" />
                  <Text style={styles.actionButtonText}>{t('accept') || 'Hyväksy'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.actionButton, styles.rejectActionButton]}
                  onPress={() => openResponseModal('reject')}
                >
                  <Ban size={20} color="#ffffff" />
                  <Text style={styles.actionButtonText}>{t('reject') || 'Hylkää'}</Text>
                </TouchableOpacity>
              </View>
            )}

            {selectedOffer.status === 'accepted' && (
              <View style={styles.actionButtons}>
                <TouchableOpacity
                  style={[styles.actionButton, styles.completeActionButton]}
                  onPress={handleMarkCompleted}
                >
                  <CheckCircle size={20} color="#ffffff" />
                  <Text style={styles.actionButtonText}>{t('mark_completed') || 'Merkitse valmiiksi'}</Text>
                </TouchableOpacity>
              </View>
            )}

            <View style={styles.bottomSpacer} />
          </ScrollView>
        </SafeAreaView>
      </Modal>
    )
  }

  const renderResponseModal = () => (
    <Modal
      visible={responseModalVisible}
      animationType="fade"
      transparent
      onRequestClose={() => setResponseModalVisible(false)}
    >
      <View style={styles.responseModalOverlay}>
        <View style={styles.responseModalContent}>
          <View style={styles.responseModalHeader}>
            <Text style={styles.responseModalTitle}>
              {responseAction === 'accept'
                ? (t('accept_offer') || 'Hyväksy tarjous')
                : (t('reject_offer') || 'Hylkää tarjous')
              }
            </Text>
            <TouchableOpacity onPress={() => setResponseModalVisible(false)}>
              <X size={24} color="#6b7280" />
            </TouchableOpacity>
          </View>

          <Text style={styles.responseModalLabel}>
            {t('message_to_seller') || 'Viesti myyjälle'} ({t('optional') || 'valinnainen'})
          </Text>
          <TextInput
            style={styles.responseInput}
            value={responseMessage}
            onChangeText={setResponseMessage}
            placeholder={
              responseAction === 'accept'
                ? (t('accept_message_placeholder') || 'Esim. Kiitos tarjouksesta! Tuontiajat löytyvät ohjeista.')
                : (t('reject_message_placeholder') || 'Esim. Emme valitettavasti pysty vastaanottamaan näitä tuotteita.')
            }
            placeholderTextColor="#9ca3af"
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />

          <View style={styles.responseModalButtons}>
            <Button variant="outline" onPress={() => setResponseModalVisible(false)}><ButtonText>{t('cancel') || 'Peruuta'}</ButtonText></Button>
            <TouchableOpacity
              style={[
                styles.confirmButton,
                responseAction === 'accept' ? styles.confirmAccept : styles.confirmReject,
              ]}
              onPress={handleSubmitResponse}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner size="small" />
              ) : (
                <Text style={styles.confirmButtonText}>
                  {responseAction === 'accept'
                    ? (t('accept') || 'Hyväksy')
                    : (t('reject') || 'Hylkää')
                  }
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  )

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}><ArrowLeft size={20} color="#748C70" /></Button>
          <Text style={styles.title}>{t('incoming_offers') || 'Saapuneet tarjoukset'}</Text>
          <View style={{ width: 40 }} />
        </View>
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}><ArrowLeft size={20} color="#748C70" /></Button>
        <Text style={styles.title}>{t('incoming_offers') || 'Saapuneet tarjoukset'}</Text>
        <View style={{ width: 40 }} />
      </View>

      {renderFilterTabs()}

      {offers.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Package size={64} color="#d1d5db" />
          <Text style={styles.emptyTitle}>
            {filterStatus === 'all'
              ? (t('no_offers_yet') || 'Ei vielä tarjouksia')
              : filterStatus === 'pending'
              ? (t('no_pending_offers') || 'Ei odottavia tarjouksia')
              : filterStatus === 'accepted'
              ? (t('no_accepted_offers') || 'Ei hyväksyttyjä tarjouksia')
              : filterStatus === 'rejected'
              ? (t('no_rejected_offers') || 'Ei hylättyjä tarjouksia')
              : (t('no_completed_offers') || 'Ei valmiita tarjouksia')
            }
          </Text>
        </View>
      ) : (
        <FlatList
          data={offers}
          renderItem={renderOfferCard}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      )}

      {renderDetailModal()}
      {renderResponseModal()}
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb' },
  backButton: {
    padding: 8 },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827' },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  filterContainer: {
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb' },
  filterContent: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8 },
  filterTab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#f3f4f6',
    marginRight: 8 },
  filterTabActive: {
    backgroundColor: '#3b82f6' },
  filterText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280' },
  filterTextActive: {
    color: '#ffffff' },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32 },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16 },
  listContent: {
    padding: 16 },
  offerCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2 },
  offerHeader: {
    flexDirection: 'row',
    alignItems: 'center' },
  offerImageContainer: {
    width: 56,
    height: 56,
    borderRadius: 8,
    overflow: 'hidden' },
  offerImage: {
    width: '100%',
    height: '100%' },
  offerImagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center' },
  offerInfo: {
    flex: 1,
    marginLeft: 12 },
  sellerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6 },
  sellerName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827' },
  itemCount: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2 },
  dateText: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 2 },
  offerRight: {
    alignItems: 'flex-end' },
  totalPrice: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4 },
  offerFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6' },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 16,
    gap: 6 },
  statusText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151' },
  quickActions: {
    flexDirection: 'row',
    gap: 8 },
  quickActionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center' },
  acceptButton: {
    backgroundColor: '#22c55e' },
  rejectButton: {
    backgroundColor: '#ef4444' },
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#ffffff' },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb' },
  closeButton: {
    padding: 8 },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827' },
  modalContent: {
    flex: 1 },
  detailSection: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6' },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8 },
  sellerInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12 },
  sellerInfoText: {
    flex: 1 },
  detailSellerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827' },
  sellerEmail: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2 },
  statusBadgeLarge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    gap: 8 },
  statusTextLarge: {
    fontSize: 15,
    fontWeight: '600',
    color: '#374151' },
  respondedDate: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 8 },
  notesBox: {
    backgroundColor: '#fef3c7',
    borderRadius: 8,
    padding: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#f59e0b' },
  notesText: {
    fontSize: 14,
    color: '#92400e',
    lineHeight: 20 },
  responseBox: {
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    padding: 12 },
  responseText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20 },
  itemCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8 },
  itemHeader: {
    flexDirection: 'row',
    alignItems: 'center' },
  itemNumber: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
    marginRight: 8 },
  itemTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '500',
    color: '#111827' },
  itemPrice: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827' },
  itemDescription: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 6,
    marginLeft: 20 },
  itemImages: {
    marginTop: 8,
    marginLeft: 20 },
  itemImage: {
    width: 60,
    height: 60,
    borderRadius: 6,
    marginRight: 8 },
  totalSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f3f4f6' },
  totalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151' },
  totalValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827' },
  actionButtons: {
    flexDirection: 'row',
    padding: 16,
    gap: 12 },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 10,
    gap: 8 },
  acceptActionButton: {
    backgroundColor: '#22c55e' },
  rejectActionButton: {
    backgroundColor: '#ef4444' },
  completeActionButton: {
    backgroundColor: '#3b82f6' },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff' },
  bottomSpacer: {
    height: 32 },
  // Response modal styles
  responseModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24 },
  responseModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 20,
    width: '100%',
    maxWidth: 400 },
  responseModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16 },
  responseModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827' },
  responseModalLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8 },
  responseInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
    color: '#111827',
    minHeight: 100,
    marginBottom: 16 },
  responseModalButtons: {
    flexDirection: 'row',
    gap: 12 },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    alignItems: 'center' },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#374151' },
  confirmButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center' },
  confirmAccept: {
    backgroundColor: '#22c55e' },
  confirmReject: {
    backgroundColor: '#ef4444' },
  confirmButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff' } })
